/**
 * جعبه‌ابزارِ ادمین روی یه رخداد/بیزینس (مایگریشن 0051).
 *
 * چرا جدا از miscRepo: هیچ‌کدوم از این‌ها روی مسیرِ کاربرِ عادی نیست - نه توی
 * فیدها صدا زده می‌شن، نه توی کشِ آفلاین می‌شینن. قاطیِ eventRepo بودنشون فقط
 * یعنی هر بار که دنبالِ کوئریِ فهرست می‌گردی، باید از روی هفت تابعِ ادمین رد شی.
 *
 * همه‌ی تغییرها از راهِ RPC می‌رن، نه update مستقیم - حتی جاهایی که RLS اجازه
 * می‌داد. دلیلش دفترِ admin_actions است: کاری که از کنارِ تابع رد شه، توی
 * دفتر ثبت نمی‌شه و بعداً «کی این رو حذف کرد» دوباره بی‌جواب می‌مونه.
 */
import { supabase, tagged } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { demoStore } from '../demo/store';
import { mapEvent } from './miscRepo';
import type { MergePick } from '../lib/tagCounts';
import type {
  AdminAction, AdminItemDetail, DiscoverMarks, DismissedDuplicate, DuplicateCandidate,
  EventItem, ItemMerge, ItemSource, MergedItem,
} from '../domain/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * برچسبی که ادمین می‌بینه: «۱۲۳۴/۲» - عددِ آیتم، ممیز، شماره‌ی نسخه.
 *
 * نسخه‌ی ۱ هم عمداً نوشته می‌شه: «۱۲۳۴» و «۱۲۳۴/۱» اگه دو شکلِ یه چیز باشن،
 * آدم موقعِ خوندنِ یکیشون باید حدس بزنه کدومه.
 */
export const itemLabel = (x: { shortId?: number | null; version?: number | null }): string =>
  (x.shortId == null ? '—' : `${x.shortId}/${x.version ?? 1}`);

/** شناسه‌های همه‌ی نسخه‌های یه آیتم - چیزی که تقریباً هر کوئریِ پایین لازم داره. */
async function threadIds(rootId: string): Promise<string[]> {
  const { data, error } = await tagged('adminRepo.threadIds', supabase
    .from('events').select('id').or(`id.eq.${rootId},root_id.eq.${rootId}`));
  if (error) throw error;
  return (data ?? []).map((r: any) => r.id as string);
}

/**
 * `jsonb` از دیتابیس می‌آید و هر مقدارش «شاید» است. این دو تا همان کاری را
 * می‌کنند که قبلاً پراکنده در چهار متد انجام می‌شد: چیزی که عدد نیست اصلاً
 * نمی‌نشیند — وگرنه `NaN` توی مرتب‌سازی یعنی ترتیبِ دلخواهِ موتور.
 */
function numberMap(raw: Record<string, unknown> | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, v] of Object.entries(raw ?? {})) {
    const n = Number(v);
    if (Number.isFinite(n)) out[id] = n;
  }
  return out;
}

function textMap(raw: Record<string, unknown> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [id, v] of Object.entries(raw ?? {})) {
    if (typeof v === 'string' && v) out[id] = v;
  }
  return out;
}

export const adminRepo = {
  /** ردیفِ کاملِ آیتم، با اسمِ آدم‌هایی که نشانش کردن یا ادغامش کردن. */
  async detail(id: string): Promise<AdminItemDetail | null> {
    if (isDemoMode) return demoStore.admin.detail(id);
    const { data, error } = await tagged('adminRepo.detail', supabase
      .from('events')
      // آخری عمداً با اسمِ **ستون** راهنمایی می‌شه نه اسمِ constraint:
      // برای رابطه‌ی یه جدول با خودش، PostgREST راهنمای constraint رو پیدا
      // نمی‌کنه ('events_merged_into_fkey' → PGRST200) ولی اسمِ ستون رو
      // می‌پذیره. برای بقیه که بینِ دو جدولِ متفاوتن، همون constraint درسته.
      .select('*, venue:venues(name,lat,lng), event_tags(tag_id), city_ref:cities(name),'
        + ' flagger:profiles!events_flagged_by_fkey(display_name),'
        + ' merger:profiles!events_merged_by_fkey(display_name),'
        + ' target:events!merged_into(short_id)')
      .eq('id', id).maybeSingle());
    if (error) throw error;
    if (!data) return null;

    const row = data as any;
    // ستون‌های joinشده جزوِ «دادهٔ خام» نیستن؛ خامْ همون ردیفِ خودِ جدوله.
    const { venue, event_tags, flagger, merger, target, city_ref, ...raw } = row;
    return {
      ...mapEvent(row),
      shortId: row.short_id,
      source: row.source ?? null,
      externalId: row.external_id ?? null,
      metadata: (row.metadata ?? {}) as Record<string, unknown>,
      flaggedAt: row.flagged_at ?? null,
      flagNote: row.flag_note ?? null,
      flaggedByName: flagger?.display_name ?? null,
      mergedInto: row.merged_into ?? null,
      mergedAt: row.merged_at ?? null,
      mergedByName: merger?.display_name ?? null,
      mergedIntoShortId: target?.short_id ?? null,
      raw: raw as Record<string, unknown>,
    };
  },

  /**
   * پیدا کردنِ آیتم با شناسه‌ی عددی.
   *
   * ردیفِ زنده رو برمی‌گردونه (وگرنه آخرین نسخه) - چون کسی که عددی رو تایپ
   * می‌کنه، خودِ آیتم رو می‌خواد نه یه نسخه‌ی بایگانی‌شده‌ش.
   */
  async byShortId(shortId: number): Promise<{ id: string; title: string; status: string } | null> {
    if (isDemoMode) return demoStore.admin.byShortId(shortId);
    const { data, error } = await tagged('adminRepo.byShortId', supabase
      .from('events').select('id, title, status, version')
      .eq('short_id', shortId));
    if (error) throw error;
    const rows = (data ?? []) as any[];
    if (!rows.length) return null;
    const best = rows.sort((a, b) => (
      Number(b.status === 'published') - Number(a.status === 'published')
      || (b.version ?? 0) - (a.version ?? 0)
    ))[0];
    return { id: best.id, title: best.title, status: best.status };
  },

  /**
   * منابعی که این آیتم رو لیست کردن.
   *
   * دو تا سرچشمه داره و هر دو لازمن: ستونِ events.source (منبعی که آورده‌ش) و
   * جدولِ event_verifications (فیدهای مستقلی که همین رو گزارش کردن، به‌علاوه‌ی
   * منبعِ هر آیتمی که توی این ادغام شده - نگاه کن به admin_merge_items).
   */
  async sources(rootId: string): Promise<ItemSource[]> {
    if (isDemoMode) return demoStore.admin.sources(rootId);
    const { data: rows, error } = await tagged('adminRepo.sources', supabase
      .from('events').select('id, source, external_id, created_at')
      .or(`id.eq.${rootId},root_id.eq.${rootId}`)
      .order('version', { ascending: true }));
    if (error) throw error;

    const out: ItemSource[] = [];
    for (const r of (rows ?? []) as any[]) {
      if (!r.source) continue;
      // یه منبع یه بار - نسخه‌های پشتِ‌سرهمِ یه فید سه‌تا ردیفِ یکسان می‌شدن.
      if (out.some((s) => s.kind === 'primary' && s.name === r.source)) continue;
      out.push({
        name: r.source, kind: 'primary', at: r.created_at ?? null,
        externalId: r.external_id ?? null,
      });
    }

    const ids = (rows ?? []).map((r: any) => r.id);
    if (!ids.length) return out;
    const { data: vers, error: vErr } = await supabase
      .from('event_verifications').select('kind, source, note, created_at')
      .in('event_id', ids).order('created_at', { ascending: true });
    if (vErr) throw vErr;

    for (const v of (vers ?? []) as any[]) {
      out.push({
        name: v.source ?? v.kind, kind: v.kind, note: v.note ?? null, at: v.created_at ?? null,
      });
    }
    return out;
  },

  /**
   * چهار نشانه‌ی ادمینِ صفحه‌ی کشف، در **یک** رفت‌وبرگشت (مایگریشن 0143).
   *
   * ═══ چرا یکی شد، وقتی چهار تا بود ═══
   *
   * تا WHOSUP-209 این‌ها چهار متدِ جدا بودند — `shortIds`،
   * `reviewPriorities`، `combinedIds` و `flaggedAt` — و هر کدام یک درخواستِ
   * جدا. اندازه‌گیریِ لاگ (`plan/whosup-209-events-select.md`) نشان داد عددِ
   * سه ثانیه‌ی صفحه‌ی کشف هزینه‌ی خودِ کوئری نیست، **انتظار در صف** است: باز
   * شدنِ صفحه حدودِ هجده رفت‌وبرگشتِ هم‌زمان می‌فرستد و در همان دسته حتی
   * `rpc.touch_device` هم سه ثانیه شده بود. پس راهِ کوتاه‌تر شدنِ صف، کم
   * کردنِ خودِ صف است.
   *
   * دو چیزِ دیگر هم با همین یکی‌شدن حل شد:
   *
   *   • **شهر.** هر چهار تا کلِ دیتابیس را می‌خواندند تا فهرستِ **یک** شهر
   *     را تزئین کنند. حالا تابع خودش شهری است و روی
   *     `events_city_status_starts` می‌نشیند.
   *   • **بریدنِ بی‌صدا.** `shortIds` و `reviewPriorities` نه `limit`
   *     داشتند نه صفحه‌بندی و سرِ ۱۰۰۰ ردیفِ PostgREST بی‌صدا بریده
   *     می‌شدند (در لاگ ۲۱ بار `rows:1000`). آیتمی که از هزار می‌افتاد
   *     بیرون، شماره‌اش دیده نمی‌شد و اولویتش صفر حساب می‌شد. تابع یک شیءِ
   *     jsonb برمی‌گرداند — یک ردیف، بی‌سقف.
   *
   * چرا این نشانه‌ها اصلاً جدا از خودِ فهرست‌اند و ستونی روی رخداد نیستند:
   * فهرستِ دیسکاور از کشِ رویِ گوشی می‌آید و کش فقط وقتی تازه می‌شود که
   * `content_hash` عوض شده باشد (نگاه کن به offlineCache). یعنی رکوردی که
   * از قبل روی گوشی نشسته هیچ‌وقت خودش `short_id` یا `flagged_at` به دست
   * نمی‌آورد؛ و پاک کردنِ کلِ کش برای این، یعنی همه‌ی کاربرها همه‌ی عکس‌ها را
   * دوباره دانلود کنند — برای چیزی که فقط ادمین می‌بیند.
   *
   * سیاستِ دسترسی جدا تضمین می‌شود: تابع `security invoker` است، پس هر
   * صداکننده دقیقاً همان ردیف‌هایی را می‌بیند که از قبل حقِ خواندنشان را
   * داشت. صدا زدنش هم فقط وقتی است که کاربر ادمین باشد.
   */
  async discoverMarks(cityId: number): Promise<DiscoverMarks> {
    if (isDemoMode) return demoStore.admin.discoverMarks();
    const { data, error } = await supabase.rpc('admin_discover_marks', { p_city_id: cityId });
    if (error) throw error;
    const raw = (data ?? {}) as {
      shortIds?: Record<string, unknown>;
      priorities?: Record<string, unknown>;
      combined?: unknown[];
      flagged?: Record<string, unknown>;
    };
    return {
      shortIds: numberMap(raw.shortIds),
      priorities: numberMap(raw.priorities),
      combined: (raw.combined ?? []).map((id) => String(id)),
      flagged: textMap(raw.flagged),
    };
  },

  /**
   * هرچی برای بازبینی نشان شده.
   *
   * بدونِ این، نشان‌کردن یه کارِ یک‌طرفه بود: می‌شد گذاشتش ولی هیچ‌جا فهرست
   * نمی‌شد، پس عملاً یادداشتی بود که هیچ‌وقت خونده نمی‌شه.
   */
  async flagged(): Promise<EventItem[]> {
    if (isDemoMode) return demoStore.admin.flagged();
    const { data, error } = await tagged('adminRepo.flagged', supabase
      .from('events')
      .select('*, venue:venues(name,lat,lng), event_tags(tag_id), city_ref:cities(name)')
      .not('flagged_at', 'is', null)
      .order('flagged_at', { ascending: false }));
    if (error) throw error;
    return ((data ?? []) as any[]).map(mapEvent);
  },

  /**
   * چیزهایی که ادغام شدن - تازه‌ترین اول.
   *
   * هیچ فهرستی نشونشون نمی‌داد: `merged` نه توی دیسکاور می‌آد (اون فقط
   * `published` می‌خواد) نه توی صفِ بازبینی. یعنی بعدِ یه ادغامِ اشتباه،
   * تنها راهِ رسیدن به دکمه‌ی «برگردون» این بود که شناسه‌ی عددیش رو از قبل
   * حفظ باشی.
   *
   * سقفِ صد تا: این یه دفترِ کاره، نه بایگانی.
   */
  async merged(): Promise<MergedItem[]> {
    if (isDemoMode) return demoStore.admin.merged();
    const { data, error } = await tagged('adminRepo.merged', supabase
      .from('events')
      .select('*, venue:venues(name,lat,lng), event_tags(tag_id), city_ref:cities(name),'
        + ' target:events!merged_into(short_id)')
      .eq('status', 'merged')
      .order('merged_at', { ascending: false })
      .limit(100));
    if (error) throw error;
    return ((data ?? []) as any[]).map((row) => ({
      ...mapEvent(row),
      mergedAt: row.merged_at ?? null,
      // شماره‌ی برنده روی خودِ ردیف نیست؛ از همون joinِ بالا می‌آد و کارت
      // بدونش فقط می‌گه «ادغام شد» و نمی‌گه با چی.
      mergedIntoShortId: row.target?.short_id ?? null,
    }));
  },

  /**
   * ردیفِ کاملِ چند آیتم با هم.
   *
   * برای مقایسه‌ی کنارِ همِ تکراری‌ها: خودِ `duplicates` فقط عنوان و تاریخ و
   * لینک می‌ده (همون‌قدر که برای یه کارتِ فهرست لازمه)، و «کدوم فیلدها فرق
   * دارن» بدونِ ردیفِ کامل جواب نداره.
   *
   * بدونِ فیلترِ `kind` و `status`: کاندیداها هم رخدادن هم مکان، و
   * `pending_review`ها دقیقاً همون‌هایی‌ان که باید بررسی شن. RLS خودش
   * می‌دونه ادمین اجازه‌ی دیدنشون رو داره.
   */
  async items(ids: string[]): Promise<EventItem[]> {
    if (!ids.length) return [];
    if (isDemoMode) return demoStore.admin.items(ids);
    const { data, error } = await tagged('adminRepo.items', supabase
      .from('events')
      .select('*, venue:venues(name,lat,lng), event_tags(tag_id), city_ref:cities(name)')
      .in('id', ids));
    if (error) throw error;
    return ((data ?? []) as any[]).map(mapEvent);
  },

  /** تکراری‌های محتمل - فقط پیشنهاد؛ ادغام همچنان دستیه. */
  async duplicates(id: string): Promise<DuplicateCandidate[]> {
    if (isDemoMode) return demoStore.admin.duplicates(id);
    const { data, error } = await supabase.rpc('admin_duplicate_candidates', { p_event_id: id });
    if (error) throw error;
    return ((data ?? []) as any[]).map((r) => ({
      id: r.id, shortId: Number(r.short_id), title: r.title, kind: r.kind,
      status: r.status, startsAt: r.starts_at ?? null, url: r.url ?? null, reason: r.reason,
    }));
  },

  /** جفت‌هایی که ادمین گفته تکراری نیستن (مایگریشن 0053). */
  async dismissedDuplicates(id: string): Promise<DismissedDuplicate[]> {
    if (isDemoMode) return demoStore.admin.dismissedDuplicates(id);
    const { data, error } = await supabase.rpc('admin_dismissed_duplicates', { p_event_id: id });
    if (error) throw error;
    return ((data ?? []) as any[]).map((r) => ({
      id: r.id, shortId: Number(r.short_id), title: r.title,
      note: r.note ?? null, createdAt: r.created_at, byName: r.by_name ?? null,
    }));
  },

  /**
   * «این دو تا یکی نیستن.»
   *
   * تنها حکمی که تا حالا جا نداشت. ادغام یعنی «آره یکی‌ان»؛ نبودِ این یعنی
   * «نه» هیچ‌جا ثبت نمی‌شد و همون جفت فردا دوباره بالای صف می‌اومد.
   */
  async dismissDuplicate(a: string, b: string, note?: string): Promise<void> {
    if (isDemoMode) return demoStore.admin.dismissDuplicate(a, b, note);
    const { error } = await supabase.rpc('admin_dismiss_duplicate', {
      p_a: a, p_b: b, p_note: note?.trim() || null,
    });
    if (error) throw error;
  },

  /** پس گرفتنِ همون حکم - جفت برمی‌گرده توی کاندیداها. */
  async restoreDuplicate(a: string, b: string): Promise<void> {
    if (isDemoMode) return demoStore.admin.restoreDuplicate(a, b);
    const { error } = await supabase.rpc('admin_restore_duplicate', { p_a: a, p_b: b });
    if (error) throw error;
  },

  /**
   * ترکیب: یه رکوردِ تازه از تکه‌های چند رکورد، و ادغامِ همه‌شون توش.
   *
   * `pick` نگاشتِ «نامِ ستون → شناسه‌ی آیتمی که مقدارش از اون برداشته می‌شه»
   * است، نه خودِ مقدار — نگاه کن به سرِ تابعِ admin_combine_items. برگشتیْ
   * شناسه‌ی رکوردِ تازه‌ست.
   */
  async combine(
    ids: string[], pick: Record<string, string>, note?: string, both: string[] = [],
  ): Promise<string> {
    if (isDemoMode) return demoStore.admin.combine(ids);
    const { data, error } = await supabase.rpc('admin_combine_items', {
      p_ids: ids, p_pick: pick, p_note: note?.trim() || null, p_both: both,
    });
    if (error) throw error;
    return data as string;
  },

  /** دفترِ کارهای ادمین روی این آیتم - همه‌ی نسخه‌هاش با هم. */
  async actions(rootId: string): Promise<AdminAction[]> {
    if (isDemoMode) return demoStore.admin.actions(rootId);
    const ids = await threadIds(rootId);
    if (!ids.length) return [];
    const { data, error } = await supabase
      .from('admin_actions')
      .select('id, action, detail, created_at, actor:profiles!admin_actions_actor_id_fkey(display_name)')
      .in('event_id', ids).order('id', { ascending: false }).limit(50);
    if (error) throw error;
    return ((data ?? []) as any[]).map((r) => ({
      id: Number(r.id), action: r.action, detail: r.detail ?? {},
      createdAt: r.created_at, actorName: r.actor?.display_name ?? null,
    }));
  },

  /** ادغام‌هایی که این آیتم توشون طرفِ ماجرا بوده - چه برنده چه بازنده. */
  async merges(rootId: string): Promise<ItemMerge[]> {
    if (isDemoMode) return demoStore.admin.merges(rootId);
    const ids = await threadIds(rootId);
    if (!ids.length) return [];
    const list = ids.join(',');
    const { data, error } = await supabase
      .from('event_merges')
      .select('id, loser_id, winner_id, note, merged_at, undone_at,'
        + ' loser:events!event_merges_loser_id_fkey(short_id,title),'
        + ' winner:events!event_merges_winner_id_fkey(short_id,title),'
        + ' by:profiles!event_merges_merged_by_fkey(display_name)')
      .or(`loser_id.in.(${list}),winner_id.in.(${list})`)
      .order('id', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as any[]).map((r) => ({
      id: Number(r.id), loserId: r.loser_id, winnerId: r.winner_id,
      loserShortId: r.loser?.short_id ?? null, loserTitle: r.loser?.title ?? null,
      winnerShortId: r.winner?.short_id ?? null, winnerTitle: r.winner?.title ?? null,
      note: r.note ?? null, mergedAt: r.merged_at, mergedByName: r.by?.display_name ?? null,
      undoneAt: r.undone_at ?? null,
    }));
  },

  /* ---------- تغییرها ---------- */

  /** حذفِ نرم. دلیل اختیاریه - برخلافِ eventRepo.reject که برای جوابِ آدمه. */
  async softDelete(id: string, reason?: string): Promise<void> {
    if (isDemoMode) return demoStore.admin.softDelete(id, reason);
    const { error } = await supabase.rpc('admin_delete_item', {
      p_event_id: id, p_reason: reason?.trim() || null,
    });
    if (error) throw error;
  },

  /** برگردوندنِ چیزی که حذف شده، به همون وضعیتی که قبلش داشت. */
  async restore(id: string): Promise<string> {
    if (isDemoMode) return demoStore.admin.restore(id);
    const { data, error } = await supabase.rpc('admin_restore_item', { p_event_id: id });
    if (error) throw error;
    return (data as string) ?? 'published';
  },

  /**
   * تعیینِ تگِ مادر — سلسله‌مراتبِ دستیِ دسته‌ها (مایگریشن 0084).
   *
   * `null` یعنی «مادر نداره». حلقه و «مادرِ خودش» رو خودِ تابعِ دیتابیس رد
   * می‌کنه، نه این‌جا: جدولِ tags هیچ policyِ update نداره و تنها راهِ
   * نوشتن همون RPC است، پس اعتبارسنجی هم باید همون‌جا باشه.
   */
  async setTagParent(tagId: number, parentId: number | null): Promise<void> {
    if (isDemoMode) return;
    const { error } = await supabase.rpc('admin_set_tag_parent', {
      p_tag: tagId, p_parent: parentId,
    });
    if (error) throw error;
  },

  /**
   * ترتیبِ ردیفِ دسته‌های صفحه‌ی کشف (مایگریشن 0088).
   *
   * سراسریه، نه پیش‌نمایشِ ادمین: همون لحظه برای همه عوض می‌شه. دو حالتش
   * توی `MergePick` توضیح داده شده.
   */
  async setTagMergePick(pick: MergePick): Promise<void> {
    if (isDemoMode) return;
    const { error } = await supabase.rpc('admin_set_setting', {
      p_key: 'tag_merge_pick', p_value: pick,
    });
    if (error) throw error;
  },

  /**
   * چند رخداد هر تگ داره (مایگریشن 0087).
   *
   * شمارش سمتِ دیتابیسه و نه این‌جا، چون PostgREST سقفِ ردیف داره
   * (`max_rows`) و `limit` بزرگ رو بی‌صدا می‌بُره — قبلاً همین باعث شده بود
   * نصفِ تگ‌ها «۰ رخداد» به‌نظر برسن و از سربرگ غیب بشن.
   *
   * عدد از پشتِ RLS در می‌آد، پس منتشرنشده‌ها رو هم می‌شمره — همون «شمارِ
   * کلِ دیتابیس» که خودِ صفحه می‌گه.
   */
  async tagUsage(): Promise<Map<number, number>> {
    if (isDemoMode) return new Map();
    const { data, error } = await supabase.rpc('admin_tag_usage');
    if (error) throw error;
    const out = new Map<number, number>();
    for (const row of (data ?? []) as { tag: number; cnt: number }[]) {
      out.set(row.tag, Number(row.cnt));
    }
    return out;
  },

  /**
   * کنار گذاشتن یا برگردوندنِ یه تگ (مایگریشن 0086).
   *
   * هیچی پاک نمی‌شه: ردیف‌هایی که این تگ رو دارن دست‌نخورده می‌مونن و فقط
   * خودِ تگ از فرم‌ها و از ردیفِ دسته‌های صفحه‌ی کشف بیرون می‌ره. `on=false`
   * همون رو برمی‌گردونه.
   */
  async setTagArchived(tagId: number, on: boolean): Promise<void> {
    if (isDemoMode) return;
    const { error } = await supabase.rpc('admin_set_tag_archived', { p_tag: tagId, p_on: on });
    if (error) throw error;
  },

  /**
   * معتبر/غیرمعتبر کردنِ یک عکس (مایگریشن 0135) — چک‌باکسِ سربرگِ «عکس‌ها».
   *
   * غیرمعتبر یعنی «دیگر به کاربر نشانش نده»، نه «پاکش کن»: آدرس سرِ جای
   * خودش روی ردیفِ رویداد می‌ماند (کرالر هم مثل قبل می‌بیندش) و فقط یک
   * پرچمِ جدا روشن می‌شود. برای همین برگرداندنش کامل است — عکس همان لحظه
   * برمی‌گردد، چون هیچ‌وقت جایی نرفته بود.
   */
  async setImageValid(url: string, valid: boolean): Promise<void> {
    if (isDemoMode) return;
    const { error } = await supabase.rpc('admin_set_image_valid', {
      p_url: url, p_valid: valid,
    });
    if (error) throw error;
  },

  /**
   * «این‌ها یکی هستند» (مایگریشن 0151) — کارتِ «چند عکسی». هر عضو آدرس‌های
   * یک رویداد است؛ `same = false` گروه‌هایی را که این آدرس‌ها در آن‌اند باز
   * می‌کند. داوری در جدولِ جداست، پس بازنویسیِ شبانه‌ی کرالر پاکش نمی‌کند.
   */
  async setSamePhotos(groups: { event_id: string; urls: string[] }[], same = true): Promise<void> {
    if (isDemoMode) return;
    const { error } = await supabase.rpc('admin_set_same_photos', {
      p_groups: groups, p_same: same,
    });
    if (error) throw error;
  },

  /** نشان‌کردن برای بازبینی - روی دیده‌شدنِ آیتم اثری نداره. */
  async setFlag(id: string, on: boolean, note?: string): Promise<void> {
    if (isDemoMode) return demoStore.admin.setFlag(id, on, note);
    const { error } = await supabase.rpc('admin_set_flag', {
      p_event_id: id, p_on: on, p_note: note?.trim() || null,
    });
    if (error) throw error;
  },

  /**
   * «بگذار کرالر دوباره مدیریتش کند» (مایگریشن 0096).
   *
   * `fields` خالی یعنی همه‌ی قفل‌ها. فهرستِ قفل‌های باقی‌مانده برمی‌گرده، پس
   * رابط بدونِ یه رفت‌وبرگشتِ دیگه می‌تونه خودش رو تازه کنه.
   *
   * ⚠ مقدارِ ستون همین‌جا به آنچه کرالر می‌گفت برنمی‌گرده — اون تاریخچه روی
   * دیتابیسِ کرالره، نه این‌جا. تا اجرای بعدیِ کرالر همون می‌مونه که ادمین
   * گذاشته بود.
   */
  async unlockFields(eventId: string, fields?: string[]): Promise<string[]> {
    if (isDemoMode) return [];
    const { data, error } = await supabase.rpc('admin_unlock_event_fields', {
      p_event_id: eventId, p_fields: fields?.length ? fields : null,
    });
    if (error) throw error;
    return (data ?? []) as string[];
  },

  /** ادغامِ این آیتم توی آیتمی که با شناسه‌ی عددی داده شده. برگشتیْ شناسه‌ی ادغامه. */
  async merge(loserId: string, winnerShortId: number, note?: string): Promise<number> {
    if (isDemoMode) return demoStore.admin.merge(loserId, winnerShortId, note);
    const { data, error } = await supabase.rpc('admin_merge_items', {
      p_loser_id: loserId, p_winner_short_id: winnerShortId, p_note: note?.trim() || null,
    });
    if (error) throw error;
    return Number(data);
  },

  /** برگردوندنِ یه ادغام - از روی همون چیزی که موقعش ثبت شده، نه حدس. */
  async unmerge(mergeId: number): Promise<void> {
    if (isDemoMode) return demoStore.admin.unmerge(mergeId);
    const { error } = await supabase.rpc('admin_unmerge', { p_merge_id: mergeId });
    if (error) throw error;
  },
};
